import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'
import { isNil } from 'ramda'

import { useSubmitPromoCodeMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import { Container } from '@twisto/components/atoms/container'
import { useForm } from '@twisto/components/atoms/form'
import {
  Headline,
  HeadlineSubtitle,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import { Loader } from '@twisto/components/atoms/loader'
import { Modal, ModalCloser } from '@twisto/components/atoms/modal'
import { TextField } from '@twisto/components/molecules/text-field'
import { FlowForm, submittingFlowFormAtom } from '@twisto/flow-views/components'
import { useResponseErrors, validator } from '@twisto/form'
import { styleUtils } from '@twisto/styles'

const fieldName = 'promoCode' as const

type FormValues = {
  [fieldName]: string
}

type Props = {
  promoCode: string | undefined | null
  opened: boolean
  sessionId: string
  onClose: () => void
}

const modalTitleId = 'promo-code-alert-dialog-title'

export const PromoCodeEditModal = ({
  sessionId,
  promoCode: defaultPromoCode,
  opened,
  onClose,
}: Props) => {
  const [submitting] = useAtom(submittingFlowFormAtom)

  const formContext = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: { promoCode: defaultPromoCode ?? '' },
  })

  const [submitPromoCode, { data }] = useSubmitPromoCodeMutation({
    onCompleted: (result) => {
      if (
        isNil(result.submitPromoCode.errors) ||
        result.submitPromoCode.errors.length === 0
      ) {
        onClose()
      }
    },
  })

  useResponseErrors(data?.submitPromoCode, formContext)

  const handleSubmitPromoCode = ({ promoCode }: FormValues) => {
    submitPromoCode({
      variables: { input: { promoCode }, auth: { sessionId } },
    })
  }

  return (
    <Modal
      aria-labelledby={modalTitleId}
      opened={opened}
      onClickOutside={submitting ? undefined : onClose}
    >
      <ModalCloser disabled={submitting} onClick={onClose} />
      <Container noPadding size="tiny">
        <Headline align="start" css={styleUtils.margin.bottom.xl}>
          <HeadlineTitle>
            <Trans id="flow.additionalInfoIntroStep.promoCode.editModal.title" />
          </HeadlineTitle>
          <HeadlineSubtitle>
            <Trans id="flow.additionalInfoIntroStep.promoCode.editModal.caption" />
          </HeadlineSubtitle>
        </Headline>
        <FlowForm {...formContext} onSubmit={handleSubmitPromoCode}>
          {submitting && <Loader />}
          <div css={styleUtils.margin.bottom.xl}>
            <TextField
              ariaLabel={
                /*i18n*/
                i18n._(
                  'flow.additionalInfoIntroStep.promoCode.editModal.promoCodeInput.inputAriaLabel'
                )
              }
              disabled={submitting}
              inputProps={{
                autoComplete: 'off',
                autoFocus: true,
              }}
              label={
                <Trans id="flow.additionalInfoIntroStep.promoCode.editModal.promoCodeInput.label" />
              }
              name={fieldName}
              rules={{ required: validator.getValidationMessages().required }}
              type="text"
            />
          </div>
          <FormSubmitButton disabled={submitting} fullWidth={true}>
            <Trans id="flow.additionalInfoIntroStep.promoCode.editModal.submit" />
          </FormSubmitButton>
        </FlowForm>
      </Container>
    </Modal>
  )
}
